export const TAG_PALETTE = [
  "#d2553f",
  "#6c63d8",
  "#1f8a5b",
  "#2f8f8a",
  "#c2823c",
  "#1c4f9c",
  "#7A2FF2",
  "#a39d90",
];

const FU_CAL_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>';

const TAGS_CELL_CAP = 2; // matches candidate_tags_display's default

const escHtml = (s: string): string => {
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
};

const escAttr = (s: string): string => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");

const parseIso = (iso: string): Date | null => {
  const [y, m, d] = (iso || "").split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "7 Aug 2026" — the grid's own Follow-up column (server: "%-d %b %Y").
const longDate = (iso: string): string => {
  const d = parseIso(iso);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : iso;
};

export function rowsFor(botsProspectId: string): HTMLElement[] {
  if (!botsProspectId) return [];
  return Array.from(document.querySelectorAll<HTMLElement>(`tr[data-bp-id="${botsProspectId}"]`));
}

/** Notes button: `created` is the "this candidate has notes" state. */
export function syncRowNotes(botsProspectId: string, hasNotes = true): void {
  if (!botsProspectId) return;
  document.querySelectorAll<HTMLElement>(`.notes-button-${botsProspectId}`).forEach((btn) => {
    btn.classList.remove("active");
    btn.classList.toggle("created", hasNotes);
  });
}

export function syncRowFollowup(emailChainId: string, isoDate: string | null, hasDraft = false): void {
  if (!emailChainId) return;
  const scheduled = Boolean(isoDate);

  document.querySelectorAll<HTMLElement>(`.schedule-button-${emailChainId}`).forEach((btn) => {
    btn.classList.remove("muted", "active", "created");
    btn.classList.add("cbtn", "fu-btn");
    btn.classList.toggle("fu-scheduled", scheduled);
    btn.classList.toggle("fu-drafted", !scheduled && hasDraft);
    btn.innerHTML = FU_CAL_SVG;
    if (scheduled) {
      btn.setAttribute("title", `Follow-up scheduled for ${isoDate}`);
    } else {
      btn.setAttribute("title", hasDraft ? "Follow-up drafted" : "Schedule follow-up");
    }

    // The grid pane also has a standalone Follow-up date column; the list pane does not.
    const cell = btn.closest("tr")?.querySelector<HTMLElement>("[data-fu-cell]");
    if (!cell) return;
    cell.innerHTML = scheduled
      ? `<span class="fu-date-cell fu-set">${escHtml(longDate(isoDate as string))}</span>`
      : '<span class="g-mut">—</span>';
  });
}

export function syncRowTags(botsProspectId: string, tags: string[], colorOffset = 0): void {
  const rows = rowsFor(botsProspectId);
  if (!rows.length) return;

  const shown = tags.slice(0, TAGS_CELL_CAP);
  const extra = tags.length - shown.length;
  const color = (i: number): string => TAG_PALETTE[(colorOffset + i) % TAG_PALETTE.length];

  const chips = shown
    .map(
      (t, i) =>
        `<span class="ctag" style="--tag-color: ${escAttr(color(i))}" title="${escAttr(t)}">` +
        `<span class="cdot2"></span><span class="text-truncate">${escHtml(t)}</span></span>`,
    )
    .join("");
  const more = extra > 0
    ? `<span class="more" title="${escAttr(tags.slice(TAGS_CELL_CAP).join(" | "))}" style="cursor: pointer;">+${extra}</span>`
    : "";
  const html = tags.length ? `<div class="ctags">${chips}${more}</div>` : "";

  rows.forEach((row) => {
    row.setAttribute("data-f-tags", tags.join(" "));
    const cell = row.querySelector<HTMLElement>("[data-tags-cell]");
    if (cell) cell.innerHTML = html;
  });
}

/**
 * Phone button: the server re-renders `bots_prospects/_phone_button` so the
 * found / not-found styling and the popover body stay defined in one place.
 * Bootstrap popovers are opt-in on this page, so the fresh button needs its own
 * init — the click handlers are all delegated on `document` and survive the swap.
 */
export function syncRowPhoneButton(botsProspectId: string, html: string): void {
  if (!botsProspectId || !html) return;

  const holder = document.createElement("div");
  holder.innerHTML = html.trim();
  const template = holder.firstElementChild as HTMLElement | null;
  if (!template) return;

  document.querySelectorAll<HTMLElement>(`.phone-button-${botsProspectId}`).forEach((button) => {
    $(button).popover("dispose");
    const fresh = template.cloneNode(true) as HTMLElement;
    button.replaceWith(fresh);
    if (fresh.matches('[data-toggle="popover"]')) {
      $(fresh).popover({ html: true, sanitize: false, trigger: "click" });
    }
  });
}
